<template>
  <main class="container-fluid">
    <header id="annuaire-header" class="gap-5">
      <div id="listing-switch-container" class="d-flex align-items-center">
        <label id="label-map" class="mbw-cursor-change mbw-switch-size form-check-label mx-2" for="listing-switch">
          Carte <i class="fa fa-map-marker" aria-hidden="true"></i>
        </label>
        <div class="mbw-switch-size mbw-switch-button form-check form-switch">
          <input @change="toggleView" class="form-check-input" type="checkbox" id="listing-switch">
        </div>
        <label id="label-list" class="mbw-cursor-change mbw-switch-size form-check-label" for="listing-switch">
          <i class="fa fa-list-ul" aria-hidden="true"></i> Annuaire
        </label>
      </div>

      <div id="close-btn">
        <a id="returnButton" title="Retourner sur la page d'accueil" href="/">
          <i class="fa fa-3x fa-times-circle" aria-hidden="true"></i>
        </a>
      </div>
    </header>

    <div class="row position-relative" id="page-content">
      <div v-if="!loaded" class="loading-screen" id="loadingScreen">
        <div class="spinner-border text-light" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>

      <LeftSection
          v-if="isDesktop && leftSectionReady"
          :loaded="Object.keys(filters).length > 0"
          :filters="filters"
          :params="this.searchContext.getAll()"
          :searchResultCount="listing ? listing.length : 0"
          @execute-search="executeSearch"
          @reset-search="resetSearch()"
      />

      <LeftSectionMobile
          v-if="!isDesktop && leftSectionReady"
          :loaded="Object.keys(filters).length > 0"
          :filters="filters"
          :params="this.searchContext.getAll()"
          :searchResultCount="listing ? listing.length : 0"
          @execute-search="executeSearch"
          @reset-search="resetSearch()"
      />

      <RightSection v-if="listing" :items="listing" :view="!view"/>
    </div>
  </main>
</template>

<script>
import SearchContext from '@/services/Search/SearchContext';

import HttpClient from '@/services/api/api';

import LeftSection from "@/components/layout/LeftSection";
import LeftSectionMobile from '@/components/layout/LeftSectionMobile';
import RightSection from "@/components/layout/RightSection";

const params = new Proxy(new URLSearchParams(window.location.search), {
  get: (searchParams, prop) => searchParams.get(prop),
});

export default {
  name: 'App',

  components: {
    LeftSection,
    LeftSectionMobile,
    RightSection
  },

  data() {
    return {
      restUrl: '',
      listing: null,
      loading: true,
      loaded: false,
      initialized: false,
      error: false,
      loadMore: false,
      searchContext: new SearchContext(),
      filters: [],
      noResults: false,
      view: false,
      isDesktop: null,
      leftSectionReady: false
    }
  },

  async created() {
    this.searchContext = new SearchContext({
      postType: params.postType,
      search: params.search,
      markerTypes: params.markerTypes
    });

    this.listing = await this.search();

    this.setIsDesktopOrMobile();
    this.leftSectionReady = true;

    this.initialized = true;
    this.loaded = true;
    this.updateLabelColors();
  },

  mounted() {
    window.addEventListener("resize", this.setIsDesktopOrMobile);
  },

  unmounted() {
    window.removeEventListener("resize", this.setIsDesktopOrMobile);
  },

  methods: {
    async executeSearch(search = null, postType = null, markerTypes = null) {
      this.searchContext.search = search;
      this.searchContext.postType = postType;
      this.searchContext.markerTypes = markerTypes;

      this.replaceSearchParams('postType', postType);
      this.replaceSearchParams('search', search);
      this.replaceSearchParams('markerTypes', markerTypes);

      this.listing = await this.search();
    },

    async resetSearch() {
      this.replaceSearchParams('postType', null);
      this.replaceSearchParams('search', null);
      this.replaceSearchParams('markerTypes', null);

      const radioButtons = this.$el.querySelectorAll(".type-select-radio");
      radioButtons.forEach(radio => {
        radio.checked = false;
      });

      const defaultRadio = this.$el.querySelector(".type-select-radio[value='tous']");
      if (defaultRadio) {
        defaultRadio.checked = true;
      }

      this.searchContext = new SearchContext({
        postType: 'tous',
        search: null,
        markerTypes: null
      });

      this.listing = await this.search();
    },

    async search() {
      let results = [];
      this.loaded = false;

      let args = {
        page: 1,
        per_page: 999,
        search: this.searchContext.search,
        acf_format: 'standard',
      }

      if (this.searchContext.markerTypes) {
        args.taxonomy = 'marker_type';
        args.terms = this.searchContext.markerTypes;
      }

      if (!this.searchContext.postType || this.searchContext.postType === 'tous') {
        results = [
          ...await HttpClient.getListingItems('wp/v2/etablissement', args),
          ...await HttpClient.getListingItems('wp/v2/service', args),
          ...await HttpClient.getListingItems('wp/v2/association', args),
          ...await HttpClient.getListingItems('wp/v2/equipement', args)
        ];
      } else {
        results = await HttpClient.getListingItems('wp/v2/' + this.searchContext.postType, args);
      }

      results = results.filter((item) => item.acf.location);

      results.sort((a, b) => Number(b.date) - Number(a.date))

      if (!args.taxonomy || args.search && !args.taxonomy || !this.initialized) {
        this.filters = await HttpClient.getCurrentFilters(results);
      }

      this.loaded = true;

      return results;
    },

    replaceSearchParams(key, value) {
      if (!value) {
        return this.removeSearchParams(key);
      }

      const url = new URL(window.location.href);
      const searchParams = url.searchParams;

      if (searchParams.has(key)) {
        searchParams.delete(key);
      }

      searchParams.set(key, value);

      history.pushState({}, null, url.toString());
    },

    removeSearchParams(key) {
      const url = new URL(window.location.href);
      const searchParams = url.searchParams;

      if (searchParams.has(key)) {
        searchParams.delete(key);
        history.pushState({}, null, url.toString());
      }
    },

    toggleView() {
      this.view = !this.view;
      this.updateLabelColors();
    },

    updateLabelColors() {
      const labelMap = document.getElementById('label-map');
      const labelList = document.getElementById('label-list');
      labelMap.classList.toggle('text-blue', !this.view);
      labelList.classList.toggle('text-blue', this.view);
    },

    setIsDesktopOrMobile() {
      this.isDesktop = window.innerWidth > 1024;
    },
  }
}
</script>

<style>
:root {
  --border-radius__10: 10px;
  --border__2sb: 2px solid black;
  --height__sections-left-n-right: 83.9vh;
  --height__sections-left-n-right-mobile: 72.2vh;
  --height__section-left-mobile: 100px;
}

/*
 * Textes styles
 */

.text-transform_unset {
  text-transform: unset !important;
}

.text-black {
  color: black;
}

.text-orange {
  color: var(--e-global-color-8dbeae2);
}

.text-blue {
  color: var(--e-global-color-secondary);
}

/*
 * Global
 */

#returnButton {
  transition: .3s;
  font-size: 15px;
}

#returnButton {
  color: var(--e-global-color-accent);
}

#returnButton:hover {
  color: var(--e-global-color-primary);
}

#close-btn {
  padding: 10px 0px;
}

/*
 * Bouton d'Accessibilité
 */

/* Met le bouton d'accessibilité en bas en droite de l'écran au lieu d'en haut à droite */
.djacc--sticky.djacc--top-right {
  top: unset !important;
  bottom: 0 !important;
}

/* Met le panel d'une meilleure façon que l'originale */
.djacc__panel.djacc__panel--active {
  position: unset !important;
  max-height: unset !important;
}

/*
 * Autres
 */

.loading-screen {
  position: absolute;
  min-height: var(--height__sections-left-n-right);
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.mbw-shadow {
  box-shadow: 0 0.125rem 1rem rgba(0, 0, 0, .075);
}

#annuaire-header {
  position: relative;
  height: 150px;
  z-index: 2;
  display: flex;
  justify-content: end;
  align-items: center;
}

.mbw-switch-size {
  font-size: 20px;
}

.mbw-switch-button {
  margin-bottom: 5px;
}

.mbw-cursor-change {
  cursor: pointer;
}

/* Enlève le fond blanc de la croix dans le sidebar left de leaflet */
#mapContainer > div.leaflet-control-container > div.leaflet-sidebar.left.visible > a {
  background: none;
}

/*
 * Mobile
 */

@media (max-width: 1024px) {
  #close-btn {
    position: fixed;
    top: 0;
    right: 10px;
  }

  #annuaire-header {
    align-items: flex-end;
    justify-content: center;
    margin-bottom: 10px;
    height: 125px;
  }

  .loading-screen {
    min-height: 84vh;
  }
}
</style>
